import * as XLSX from 'xlsx'
import type { PlotImportRowInput } from '@riverpark/types'
import { PLOT_IMPORT_MAX_ROWS } from '@riverpark/types'
import {
  isSpecialUsePlotNumber,
  mapPlotImportHeaderFields,
  PLOT_IMPORT_SHEET_COLUMNS,
  PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW,
} from '@riverpark/utils/plot-import'

export {
  PLOT_IMPORT_SHEET_COLUMNS,
  PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW,
} from '@riverpark/utils/plot-import'

export const IMPORT_TEMPLATE_HEADERS = PLOT_IMPORT_SHEET_COLUMNS.map((c) => c.header)

function cellValue(row: unknown[], index: number | undefined): string {
  if (index === undefined) return ''
  const value = row[index]
  if (value == null) return ''
  return String(value).trim()
}

function escapeCsvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function downloadPlotImportTemplateCsv() {
  const lines = [
    IMPORT_TEMPLATE_HEADERS.map(escapeCsvField).join(','),
    PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW.map(escapeCsvField).join(','),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, 'riverpark-plot-import-template.csv')
}

export function downloadPlotImportTemplateExcel() {
  const sheet = XLSX.utils.aoa_to_sheet([IMPORT_TEMPLATE_HEADERS, PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'Plots')
  XLSX.writeFile(workbook, 'riverpark-plot-import-template.xlsx')
}

const SKIP_PLOT_NUMBER_PATTERNS = [
  /^total residential/i,
  /^special-use areas/i,
  /^plot no\.?$/i,
  /^mhasibu/i,
]

function shouldSkipRow(plotNumber: string, courtOrStreet: string): boolean {
  const plot = plotNumber.trim()
  if (!plot && !courtOrStreet.trim()) return true
  if (SKIP_PLOT_NUMBER_PATTERNS.some((p) => p.test(plot))) return true
  return false
}

export async function parsePlotImportFile(file: File): Promise<PlotImportRowInput[]> {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) throw new Error('The file has no worksheets')

  const sheet = workbook.Sheets[sheetName]
  if (!sheet) throw new Error('Could not read the worksheet')

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' })
  if (rows.length < 2)
    throw new Error('The file must include a header row and at least one plot row')

  let headerRowIndex = -1
  let map: ReturnType<typeof mapPlotImportHeaderFields> = {}

  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const row = rows[i]
    if (!Array.isArray(row)) continue
    const headers = row.map((h) => String(h ?? ''))
    const candidate = mapPlotImportHeaderFields(headers)
    if (candidate.plotNumber !== undefined) {
      headerRowIndex = i
      map = candidate
      break
    }
  }

  if (headerRowIndex < 0 || map.plotNumber === undefined) {
    throw new Error('The file must include a "Plot No." column')
  }

  const dataRows = rows.slice(headerRowIndex + 1).filter((row) => {
    if (!Array.isArray(row)) return false
    const plotNumber = cellValue(row, map.plotNumber)
    const courtOrStreet = cellValue(row, map.courtOrStreet)
    if (shouldSkipRow(plotNumber, courtOrStreet)) return false
    if (!plotNumber && !courtOrStreet) return false
    if (isSpecialUsePlotNumber(plotNumber) && !courtOrStreet) return false
    return true
  })

  if (dataRows.length === 0) throw new Error('No plot rows found in the file')
  if (dataRows.length > PLOT_IMPORT_MAX_ROWS) {
    throw new Error(`Import is limited to ${PLOT_IMPORT_MAX_ROWS} plots per file`)
  }

  return dataRows.map((row) => {
    if (!Array.isArray(row)) throw new Error('Invalid row in file')
    return {
      rowId: crypto.randomUUID(),
      plotNumber: cellValue(row, map.plotNumber),
      mainAvenue: cellValue(row, map.mainAvenue) || undefined,
      courtOrStreet: cellValue(row, map.courtOrStreet) || undefined,
      section: cellValue(row, map.section) || undefined,
      notes: cellValue(row, map.notes) || undefined,
      boreholeMeterNumber: cellValue(row, map.boreholeMeterNumber) || undefined,
      kplcMeterNumber: cellValue(row, map.kplcMeterNumber) || undefined,
    }
  })
}
